import { z } from "zod";
import { SlideSkillEnum } from "./slide";

const ProjectItemCountResultSchema = z.object({
  projects: z.number(),
  lessons: z.number(),
  audioTracks: z.number(),
  /** True if any Audio-kind child project has at least one real track — an empty audio folder doesn't count. */
  hasAudio: z.boolean(),
});

export type ProjectItemCountResult = z.infer<typeof ProjectItemCountResultSchema>;

/**
 * What a project holds.
 * - normal: the usual folder of child projects and lessons
 * - audio: a folder of audio tracks only; cannot contain projects or lessons
 *
 * Documents created before this field existed have no `kind` at all, so reads
 * default to "normal" and queries for normal projects must match `{ $ne: "audio" }`
 * rather than `{ $eq: "normal" }`.
 */
export const ProjectKindSchema = z.enum(["normal", "audio"]);

export type ProjectKind = z.infer<typeof ProjectKindSchema>;

/**
 * A named group owned by one project, covering the lessons directly under it. `useGroup`
 * decides whether clients (e.g. b2b-mobile) render slides grouped by these.
 */
export const ProjectGroupSchema = z.object({
  id: z.string(),
  name: z.string(),
  // A group claims a slide whose skill, or any of whose tags, is listed here.
  skills: z.array(SlideSkillEnum).default([]),
  tagIds: z.array(z.string()).default([]),
  randomiseOrder: z.boolean().default(false),
  sortOrder: z.number().nullable().optional(),
});

export type ProjectGroup = z.infer<typeof ProjectGroupSchema>;

export const ProjectSchema = z.object({
  id: z.string(),
  name: z.string(),
  parentId: z.string().nullable(),
  originalProjectId: z.string().nullable(),
  isHidden: z.boolean(),
  schoolIds: z.array(z.string()),
  productId: z.string(),
  // Defaults to [] for documents predating the field; readers derive [productId] instead.
  productIds: z.array(z.string()).default([]),
  isTrash: z.boolean(),
  isPublic: z.boolean(),
  imageId: z.string().nullable(),
  // Internal/cached field, not exposed on the GraphQL Project type — must be optional, not
  // just nullable, so clients validating a GraphQL response never fail on its absence.
  imageKey: z.string().nullable().optional(),
  isDeleted: z.boolean(),
  deletedAt: z.date().nullable().optional(),
  level: z.number(),
  itemCount: z.number().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  isLinkedCopy: z.boolean().default(false),
  sortOrder: z.number().nullable(),
  // Owned per project, seeded from the parent at creation.
  // Absent on legacy documents, so default rather than failing validation on read.
  groups: z.array(ProjectGroupSchema).default([]),
  useGroup: z.boolean().default(false),
  kind: ProjectKindSchema.default("normal"),
});

export const ProjectsSchema = z.array(ProjectSchema);

export type Project = z.infer<typeof ProjectSchema>;
